type L = { tr: string; en: string };

export type BlogFaqItem = {
  question: L;
  answer: L;
};

export type BlogConversionContext = {
  projectSlugs: string[];
  toolIds: string[];
  playbookSlugs: string[];
  faqs: BlogFaqItem[];
};

const defaultFaqs: BlogFaqItem[] = [
  {
    question: {
      tr: 'Figtures ile nasıl başlarız?',
      en: 'How do we get started with Figtures?',
    },
    answer: {
      tr: '15 dakikalık discovery görüşmesiyle hedefinizi, kapsamı ve zaman çizelgesini netleştiriyoruz. Ardından net backlog ve faz planı paylaşıyoruz.',
      en: 'We start with a 15-minute discovery call to clarify goals, scope, and timeline. Then we share an explicit backlog and phase plan.',
    },
  },
  {
    question: {
      tr: 'Uzak ekiplerle çalışıyor musunuz?',
      en: 'Do you work with remote teams?',
    },
    answer: {
      tr: 'Evet. Figtures remote-first bir product studio olarak Türkiye, Avrupa ve global startup/kurumsal ekiplerle çalışır.',
      en: 'Yes. Figtures is a remote-first product studio working with teams in Turkey, Europe, and worldwide.',
    },
  },
  {
    question: {
      tr: 'Ücretsiz araçlarınız var mı?',
      en: 'Do you offer free tools?',
    },
    answer: {
      tr: 'Evet — proje maliyeti, ROI ve ATS CV analizi gibi 22+ ücretsiz AI aracımız /tools adresinde.',
      en: 'Yes — 22+ free AI tools including project cost, ROI, and ATS CV analysis at /tools.',
    },
  },
];

const slugOverrides: Record<string, Omit<BlogConversionContext, 'faqs'> & { extraFaqs?: BlogFaqItem[] }> = {
  'custom-software-development-company-guide': {
    projectSlugs: ['ztech', 'lcw'],
    toolIds: ['project-cost-estimator', 'roi-calculator'],
    playbookSlugs: ['ai-assisted-product-development-approach'],
    extraFaqs: [
      {
        question: {
          tr: 'Özel yazılım projesi ne kadar sürer?',
          en: 'How long does custom software take?',
        },
        answer: {
          tr: 'Discovery sonrası MVP genelde 8–14 hafta; kapsam netleştikçe faz planı güncellenir.',
          en: 'After discovery, MVP delivery is typically 8–14 weeks; the phase plan updates as scope clarifies.',
        },
      },
    ],
  },
  'ai-development-company-selection-guide': {
    projectSlugs: ['ztech', '3d-yaninda'],
    toolIds: ['project-cost-estimator', 'ats-cv-analyzer'],
    playbookSlugs: ['chatgpt-vs-claude-workflow-selection'],
    extraFaqs: [
      {
        question: {
          tr: 'PoC’ten production’a geçişi yapıyor musunuz?',
          en: 'Do you move from PoC to production?',
        },
        answer: {
          tr: 'Evet — guardrail, maliyet kontrolü ve ürün entegrasyonu ile production AI teslimi bizim ana hizmetlerimizden biri.',
          en: 'Yes — production AI with guardrails, cost control, and product integration is a core delivery lane for us.',
        },
      },
    ],
  },
  'saas-development-cost-guide': {
    projectSlugs: ['bool', 'backdoor-tickets'],
    toolIds: ['project-cost-estimator', 'roi-calculator'],
    playbookSlugs: ['scoping-ai-integrations-for-web-and-mobile'],
    extraFaqs: [
      {
        question: {
          tr: 'SaaS MVP bütçesi nasıl hesaplanır?',
          en: 'How is SaaS MVP budget estimated?',
        },
        answer: {
          tr: 'Ücretsiz proje maliyet aracımızla ilk aralığı alın; discovery sprint sonrası netleştiririz.',
          en: 'Use our free project cost tool for a first range; we refine after a discovery sprint.',
        },
      },
    ],
  },
  'mobile-app-development-cost-guide': {
    projectSlugs: ['ailem-aselsan', 'outfica'],
    toolIds: ['project-cost-estimator', 'aso-description-generator'],
    playbookSlugs: ['scoping-ai-integrations-for-web-and-mobile'],
  },
  'chatgpt-integration-business-guide': {
    projectSlugs: ['ztech', 'lcw'],
    toolIds: ['customer-reply-generator', 'job-posting-writer'],
    playbookSlugs: ['chatgpt-vs-claude-workflow-selection'],
    extraFaqs: [
      {
        question: {
          tr: 'ChatGPT entegrasyonu chatbot mu?',
          en: 'Is ChatGPT integration just a chatbot?',
        },
        answer: {
          tr: 'Hayır — ürün içi asistan, RAG, destek triage ve operasyon otomasyonu kapsar; widget-only değil.',
          en: 'No — it covers in-product assistants, RAG, support triage, and ops automation, not widget-only.',
        },
      },
    ],
  },
  'mvp-development-company-guide': {
    projectSlugs: ['kolektif', 'bool'],
    toolIds: ['project-cost-estimator', 'pitch-evaluator'],
    playbookSlugs: ['ai-assisted-product-development-approach'],
  },
  'ux-design-in-ai-products': {
    projectSlugs: ['outfica', 'lcw'],
    toolIds: ['customer-reply-generator', 'brand-kit-generator'],
    playbookSlugs: ['ai-assisted-product-development-approach'],
  },
  'saas-scaling-strategies': {
    projectSlugs: ['bool', 'ztech'],
    toolIds: ['roi-calculator', 'project-cost-estimator'],
    playbookSlugs: ['release-geometry-progressive-risk-slicing'],
  },
  'modern-fullstack-tooling': {
    projectSlugs: ['ztech', '3d-yaninda'],
    toolIds: ['project-cost-estimator'],
    playbookSlugs: ['developer-experience-as-architecture'],
  },
  'mvp-development-guide': {
    projectSlugs: ['kolektif', 'backdoor-tickets'],
    toolIds: ['pitch-evaluator', 'project-cost-estimator'],
    playbookSlugs: ['ai-assisted-product-development-approach'],
  },
  'mobile-app-performance-optimization': {
    projectSlugs: ['ailem-aselsan', 'outfica'],
    toolIds: ['aso-description-generator', 'project-cost-estimator'],
    playbookSlugs: ['scoping-ai-integrations-for-web-and-mobile'],
  },
  'data-security-and-compliance': {
    projectSlugs: ['lcw', 'ailem-aselsan'],
    toolIds: ['roi-calculator'],
    playbookSlugs: ['release-geometry-progressive-risk-slicing'],
  },
};

const fallbackContext: Omit<BlogConversionContext, 'faqs'> = {
  projectSlugs: ['ztech', '3d-yaninda', 'lcw'],
  toolIds: ['project-cost-estimator', 'roi-calculator'],
  playbookSlugs: ['ai-assisted-product-development-approach'],
};

export function getBlogConversionContext(slug: string): BlogConversionContext {
  const override = slugOverrides[slug];
  const base = override ?? fallbackContext;
  const faqs = [...defaultFaqs, ...(override?.extraFaqs ?? [])].slice(0, 5);

  return {
    projectSlugs: base.projectSlugs.slice(0, 3),
    toolIds: base.toolIds.slice(0, 2),
    playbookSlugs: base.playbookSlugs.slice(0, 2),
    faqs,
  };
}

export function getBlogConversionCtaCopy(lang: 'tr' | 'en') {
  return lang === 'tr'
    ? {
        title: 'Projenizi birlikte netleştirelim',
        subtitle:
          'Formu doldurun — 24 saat içinde ön kapsam özeti ve bütçe aralığı. İsterseniz discovery görüşmesi planlarız.',
        primary: 'Ön kapsam talep et',
        toolsTitle: 'Önce kendi kendinize tahmin alın',
        toolsSubtitle: 'Ücretsiz AI araçlarımızla maliyet ve ROI aralığını dakikalar içinde görün.',
        projectsTitle: 'Benzer işlerden örnekler',
      }
    : {
        title: 'Let’s scope your project together',
        subtitle:
          'Submit the form — scope outline and budget range within 24 hours. We can schedule a discovery call if you prefer.',
        primary: 'Request scope outline',
        toolsTitle: 'Estimate on your own first',
        toolsSubtitle: 'Use our free AI tools to see cost and ROI ranges in minutes.',
        projectsTitle: 'Similar work from our portfolio',
      };
}
